import React, { useCallback, useEffect, useState, useRef } from "react";
import "../styles/AdminGallery.css";

import { GALLERY_API, getImageUrl } from "../config/api";
import { showConfirm } from "../utils/customDialog";

function AdminGallery() {
  const [albums, setAlbums] = useState([]);
  const [activeAlbum, setActiveAlbum] = useState(null);

  const [newAlbum, setNewAlbum] = useState("");
  const [editAlbumId, setEditAlbumId] = useState(null);
  const [editAlbumName, setEditAlbumName] = useState("");

  const [mediaItems, setMediaItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [activeTab, setActiveTab] = useState("albums-catalog");

  // Multi-file and Folder Upload states
  const [stagedFiles, setStagedFiles] = useState([]);
  const [stagedFolderName, setStagedFolderName] = useState("");
  const [previewThumbnails, setPreviewThumbnails] = useState([]);
  const [folderAutoAlbum, setFolderAutoAlbum] = useState(false);
  const [customAlbumName, setCustomAlbumName] = useState("");
  const [uploadProgress, setUploadProgress] = useState({
    inProgress: false,
    total: 0,
    uploaded: 0,
    percent: 0,
    statusText: "",
  });
  const [isDragging, setIsDragging] = useState(false);

  // Hidden file inputs
  const folderInputRef = useRef(null);
  const filesInputRef = useRef(null);

  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute("webkitdirectory", "");
      folderInputRef.current.setAttribute("directory", "");
    }
  }, []);

  // ================= UNLINK OTHER ADMIN CSS =================
  

  // ================= LOAD ALBUMS =================
  const fetchAlbums = useCallback(async () => {
    try {
      const res = await fetch(`${GALLERY_API}?action=get_albums`);
      const data = await res.json();

      const safeAlbums = Array.isArray(data) ? data : [];

      setAlbums(safeAlbums);

      if (safeAlbums.length > 0 && !activeAlbum) {
        setActiveAlbum(safeAlbums[0].id);
      }
    } catch (err) {
      console.error("Album fetch error:", err);
    }
  }, [activeAlbum]);

  // ================= LOAD MEDIA =================
  const fetchMedia = useCallback(async (albumId) => {
    if (!albumId) return;

    setLoading(true);

    try {
      const res = await fetch(
        `${GALLERY_API}?action=get_media&album_id=${albumId}`
      );

      const data = await res.json();

      setMediaItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Media fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  useEffect(() => {
    if (activeAlbum) {
      fetchMedia(activeAlbum);
    }
  }, [activeAlbum, fetchMedia]);

  // ================= CREATE ALBUM =================
  const addAlbum = async () => {
    if (!newAlbum.trim()) {
      alert("Album name required");
      return;
    }

    try {
      const res = await fetch(`${GALLERY_API}?action=create_album`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newAlbum,
        }),
      });

      const result = await res.json();

      if (result?.success || result) {
        setNewAlbum("");
        fetchAlbums();
        setActiveTab("albums-catalog");
      } else {
        alert("Failed to create album");
      }
    } catch (err) {
      console.error("Create album error:", err);
    }
  };

  // ================= DELETE ALBUM =================
  const deleteAlbum = async (id, albumName) => {
    const targetName = albumName || albums.find((a) => a.id === id)?.name || "this folder";
    const ok = await showConfirm({
      title: "Delete Album Folder",
      message: `Are you sure you want to delete the folder "${targetName}"?`,
      subMessage: "All photos and images inside this album will be permanently deleted.",
      confirmText: "Delete Folder",
      type: "danger"
    });
    if (!ok) return;

    try {
      const res = await fetch(`${GALLERY_API}?action=delete_album`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      const result = await res.json();

      if (result?.success || result) {
        const remaining = albums.filter((a) => a.id !== id);
        setAlbums(remaining);

        if (activeAlbum === id) {
          const nextActive = remaining.length > 0 ? remaining[0].id : null;
          setActiveAlbum(nextActive);
          if (nextActive) {
            fetchMedia(nextActive);
          } else {
            setMediaItems([]);
          }
        }

        fetchAlbums();
        alert(`Folder "${targetName}" deleted successfully! ✅`);
      } else {
        alert("Delete failed: " + (result?.message || "Server error"));
      }
    } catch (err) {
      console.error("Delete album error:", err);
      alert("Error deleting album: " + (err.message || "Network error"));
    }
  };

  // ================= UPDATE ALBUM =================
  const updateAlbum = async () => {
    if (!editAlbumId || !editAlbumName.trim()) {
      return;
    }

    try {
      const res = await fetch(`${GALLERY_API}?action=update_album`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: editAlbumId,
          name: editAlbumName,
        }),
      });

      const result = await res.json();

      if (result?.success || result) {
        setEditAlbumId(null);
        setEditAlbumName("");

        fetchAlbums();
      } else {
        alert("Update failed");
      }
    } catch (err) {
      console.error("Update album error:", err);
    }
  };

  // ================= PROCESS SELECTED FILES (FOLDER / MULTI-FILE / DROP) =================
  const processSelectedFiles = (fileList, detectedFolder = "") => {
    if (!fileList || fileList.length === 0) return;

    const allFiles = Array.from(fileList);
    const validImages = allFiles.filter((f) => {
      const isImgType = f.type && f.type.startsWith("image/");
      const isImgExt = /\.(jpe?g|png|webp|gif|bmp|svg)$/i.test(f.name);
      const isHidden = f.name.startsWith(".");
      return (isImgType || isImgExt) && !isHidden;
    });

    if (validImages.length === 0) {
      alert("No valid image files found. Supported formats: JPG, PNG, WEBP, and GIF.");
      return;
    }

    let folderName = detectedFolder;
    if (!folderName && validImages[0]?.webkitRelativePath) {
      const parts = validImages[0].webkitRelativePath.split("/");
      if (parts.length > 1) {
        folderName = parts[0];
      }
    }

    previewThumbnails.forEach((t) => URL.revokeObjectURL(t.url));

    const previews = validImages.slice(0, 12).map((f) => ({
      name: f.name,
      url: URL.createObjectURL(f),
      size: (f.size / (1024 * 1024)).toFixed(2),
    }));

    setStagedFiles(validImages);
    setStagedFolderName(folderName || "");
    setCustomAlbumName(folderName || "");
    const matchingAlbum = albums.find((a) => a.name.toLowerCase() === (folderName || "").toLowerCase());
    if (matchingAlbum) {
      setActiveAlbum(matchingAlbum.id);
      setFolderAutoAlbum(false);
    } else {
      setFolderAutoAlbum(Boolean(folderName));
    }
    setPreviewThumbnails(previews);

    setActiveTab("manage-albums");
  };

  const handleFolderSelect = (e) => {
    const files = e.target.files;
    let folder = "";
    if (files && files[0]?.webkitRelativePath) {
      folder = files[0].webkitRelativePath.split("/")[0];
    }
    processSelectedFiles(files, folder);
    e.target.value = "";
  };

  const handleFilesSelect = (e) => {
    processSelectedFiles(e.target.files);
    e.target.value = "";
  };

  const clearStagedFiles = () => {
    previewThumbnails.forEach((t) => URL.revokeObjectURL(t.url));
    setStagedFiles([]);
    setStagedFolderName("");
    setCustomAlbumName("");
    setFolderAutoAlbum(false);
    setPreviewThumbnails([]);
  };

  // ================= DRAG & DROP HANDLERS =================
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    const files = e.dataTransfer.files;

    if (items && items.length > 0) {
      const extractedFiles = [];
      let detectedFolder = "";

      const readEntry = async (entry, path = "") => {
        if (entry.isFile) {
          return new Promise((resolve) => {
            entry.file((file) => {
              file.filepath = path + file.name;
              extractedFiles.push(file);
              resolve();
            });
          });
        } else if (entry.isDirectory) {
          if (!detectedFolder) detectedFolder = entry.name;
          const dirReader = entry.createReader();
          return new Promise((resolve) => {
            const readEntries = () => {
              dirReader.readEntries(async (entries) => {
                if (entries.length === 0) {
                  resolve();
                } else {
                  for (const ent of entries) {
                    await readEntry(ent, path + entry.name + "/");
                  }
                  readEntries();
                }
              });
            };
            readEntries();
          });
        }
      };

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.webkitGetAsEntry) {
          const entry = item.webkitGetAsEntry();
          if (entry) {
            await readEntry(entry);
          }
        }
      }

      if (extractedFiles.length > 0) {
        processSelectedFiles(extractedFiles, detectedFolder);
        return;
      }
    }

    if (files && files.length > 0) {
      processSelectedFiles(files);
    }
  };

  // ================= CLIENT-SIDE OPTIMIZATION HELPER =================
  // Resizes oversized DSLR/camera/phone photos (> 1.5MB) to max 2048px (85% JPEG)
  // Prevents Nginx/proxy 413 "Request Entity Too Large" and "Failed to fetch" drops
  const optimizeImageForUpload = async (file) => {
    if (!file || file.size <= 1.5 * 1024 * 1024 || file.type === "image/svg+xml" || file.type === "image/gif") {
      return file;
    }
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const MAX_DIM = 2048;
          let width = img.width;
          let height = img.height;

          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) {
              height = Math.round((height * MAX_DIM) / width);
              width = MAX_DIM;
            } else {
              width = Math.round((width * MAX_DIM) / height);
              height = MAX_DIM;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob && blob.size < file.size) {
                const cleanName = file.name.replace(/\.[^.]+$/, ".jpg");
                const optimized = new File([blob], cleanName, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(optimized);
              } else {
                resolve(file);
              }
            },
            "image/jpeg",
            0.85
          );
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(file);
        };
        img.src = url;
      });
    } catch {
      return file;
    }
  };

  // ================= BATCH & FOLDER UPLOAD EXECUTION =================
  const uploadBatchMedia = async () => {
    if (!stagedFiles || stagedFiles.length === 0) {
      alert("Please select images or a folder to upload.");
      return;
    }

    let targetAlbumId = activeAlbum;
    let targetAlbumName = "";

    if (folderAutoAlbum && customAlbumName.trim()) {
      targetAlbumName = customAlbumName.trim();
    } else {
      if (!targetAlbumId) {
        alert("Please select a target album category or choose 'Create new album'.");
        return;
      }
      const existing = albums.find((a) => a.id === targetAlbumId);
      targetAlbumName = existing?.name || "Album";
    }

    setUploading(true);
    const totalFiles = stagedFiles.length;
    setUploadProgress({
      inProgress: true,
      total: totalFiles,
      uploaded: 0,
      percent: 0,
      statusText: `Preparing ${totalFiles} photos for upload...`,
    });

    // Chunk size: 3 photos per request ensures total payload stays small (< 3MB)
    // and never exceeds server or proxy limits even on slow connections
    const CHUNK_SIZE = 3;
    let uploadedCount = 0;
    let resolvedAlbumId = folderAutoAlbum ? null : targetAlbumId;

    try {
      for (let i = 0; i < totalFiles; i += CHUNK_SIZE) {
        const chunk = stagedFiles.slice(i, i + CHUNK_SIZE);
        const chunkStart = uploadedCount + 1;
        const chunkEnd = Math.min(uploadedCount + chunk.length, totalFiles);

        setUploadProgress({
          inProgress: true,
          total: totalFiles,
          uploaded: uploadedCount,
          percent: Math.round((uploadedCount / totalFiles) * 100),
          statusText: `Preparing & optimizing photos ${chunkStart} to ${chunkEnd} of ${totalFiles}...`,
        });

        // Optimize any large camera files in this chunk before sending
        const optimizedChunk = [];
        for (const f of chunk) {
          const opt = await optimizeImageForUpload(f);
          optimizedChunk.push(opt);
        }

        const formData = new FormData();
        if (resolvedAlbumId) {
          formData.append("album_id", resolvedAlbumId);
        } else if (targetAlbumName) {
          formData.append("new_album_name", targetAlbumName);
          formData.append("auto_create_album", "true");
        }

        optimizedChunk.forEach((file) => {
          formData.append("files", file);
        });

        setUploadProgress({
          inProgress: true,
          total: totalFiles,
          uploaded: uploadedCount,
          percent: Math.round((uploadedCount / totalFiles) * 100),
          statusText: `Uploading photos ${chunkStart} to ${chunkEnd} of ${totalFiles}...`,
        });

        let success = false;
        let lastError = null;

        // Auto-retry up to 2 times if network or proxy connection drops
        for (let attempt = 1; attempt <= 2 && !success; attempt++) {
          try {
            const res = await fetch(`${GALLERY_API}?action=upload_batch`, {
              method: "POST",
              body: formData,
            });

            if (!res.ok) {
              const errText = await res.text();
              throw new Error(`Server returned HTTP ${res.status}: ${errText.slice(0, 100)}`);
            }

            const result = await res.json();
            if (!result.success) {
              throw new Error(result.message || "Failed uploading batch chunk");
            }

            if (result.album_id) {
              resolvedAlbumId = result.album_id;
            }
            success = true;
          } catch (err) {
            lastError = err;
            if (attempt < 2) {
              setUploadProgress((prev) => ({
                ...prev,
                statusText: `Network glitch, retrying photos ${chunkStart}-${chunkEnd}...`,
              }));
              await new Promise((r) => setTimeout(r, 2000));
            }
          }
        }

        if (!success) {
          throw lastError || new Error("Failed uploading photos");
        }

        uploadedCount += chunk.length;
        setUploadProgress({
          inProgress: true,
          total: totalFiles,
          uploaded: uploadedCount,
          percent: Math.round((uploadedCount / totalFiles) * 100),
          statusText: `Uploaded ${uploadedCount} of ${totalFiles} photos (${Math.round((uploadedCount / totalFiles) * 100)}%)...`,
        });
      }

      setUploadProgress({
        inProgress: false,
        total: totalFiles,
        uploaded: totalFiles,
        percent: 100,
        statusText: `✅ All ${totalFiles} photos uploaded successfully!`,
      });

      clearStagedFiles();

      await fetchAlbums();
      if (resolvedAlbumId) {
        setActiveAlbum(resolvedAlbumId);
        fetchMedia(resolvedAlbumId);
      } else if (activeAlbum) {
        fetchMedia(activeAlbum);
      }

      setActiveTab("albums-catalog");
      alert(`🎉 Successfully uploaded ${totalFiles} photo(s) to "${targetAlbumName || "Album"}"!`);
    } catch (err) {
      console.error("Batch upload error:", err);
      alert("Upload error: " + (err.message || "Failed to upload all images"));
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress((prev) => ({ ...prev, inProgress: false }));
      }, 3500);
    }
  };

  // ================= DELETE MEDIA =================
  const deleteMedia = async (id) => {
    const ok = await showConfirm({
      title: "Delete Image",
      message: "Are you sure you want to delete this image?",
      subMessage: "This photo will be permanently removed from the gallery.",
      confirmText: "Delete Image",
      type: "danger"
    });
    if (!ok) return;

    try {
      const res = await fetch(`${GALLERY_API}?action=delete_media`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      const result = await res.json();

      if (result?.success || result) {
        setMediaItems((prev) =>
          prev.filter((item) => item.id !== id)
        );
      } else {
        alert("Delete failed ❌");
      }
    } catch (err) {
      console.error("Delete media error:", err);
    }
  };

  // ================= SET ALBUM COVER =================
  const setAsAlbumCover = async (filePath) => {
    if (!activeAlbum || !filePath) return;
    try {
      const res = await fetch(`${GALLERY_API}?action=set_album_cover`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          album_id: activeAlbum,
          file_path: filePath,
        }),
      });

      const result = await res.json();
      if (result?.success) {
        setAlbums((prev) =>
          prev.map((a) =>
            a.id === activeAlbum
              ? { ...a, cover_image: filePath, effective_cover_image: filePath }
              : a
          )
        );
        alert("✨ Successfully updated folder cover photo!");
      } else {
        alert("Failed to set cover photo: " + (result?.message || "Server error"));
      }
    } catch (err) {
      console.error("Set cover error:", err);
      alert("Error setting cover photo: " + (err.message || "Network error"));
    }
  };

  return (
    <div className="admin-container gallery-admin-container">
      {/* Hidden file inputs for Entire Folder & Multi-Files */}
      <input
        type="file"
        ref={folderInputRef}
        webkitdirectory=""
        directory=""
        multiple
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFolderSelect}
      />
      <input
        type="file"
        ref={filesInputRef}
        multiple
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFilesSelect}
      />

      {/* Header with Dashboard Metrics */}
      <div className="gallery-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-images"></i> Gallery & Album Manager
          </h1>
          <p>Organize spiritual photo albums, manage media, and upload new gallery items</p>
        </div>

        <div className="gallery-metrics-grid">
          <div className="metric-card" onClick={() => setActiveTab("albums-catalog")} style={{ cursor: "pointer" }}>
            <div className="metric-icon albums"><i className="bi bi-folder-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{albums.length}</span>
              <span className="metric-label">Total Albums</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon media"><i className="bi bi-image-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{mediaItems.length}</span>
              <span className="metric-label">Photos in Album</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon active-album-name"><i className="bi bi-journal-album"></i></div>
            <div className="metric-info">
              <span className="metric-value" style={{ fontSize: "14px", fontWeight: "750" }}>
                {albums.find((a) => a.id === activeAlbum)?.name || "None"}
              </span>
              <span className="metric-label">Active Album</span>
            </div>
          </div>
        </div>
      </div>

      {/* PAGE TABS NAVIGATION */}
      <div className="gallery-tabs">
        <button
          type="button"
          className={`gallery-tab-btn ${activeTab === "albums-catalog" ? "active" : ""}`}
          onClick={() => setActiveTab("albums-catalog")}
        >
          <i className="bi bi-grid-3x3-gap"></i>
          <span className="tab-text-container">
            <strong>Album Catalog</strong>
            <span className="tab-subtext">View albums & media grid</span>
          </span>
        </button>

        <button
          type="button"
          className={`gallery-tab-btn ${activeTab === "manage-albums" ? "active" : ""}`}
          onClick={() => setActiveTab("manage-albums")}
        >
          <i className="bi bi-plus-circle"></i>
          <span className="tab-text-container">
            <strong>Manage & Upload</strong>
            <span className="tab-subtext">Create albums & upload photos</span>
          </span>
        </button>
      </div>

      {/* ================= ALBUMS CATALOG TAB ================= */}
      {activeTab === "albums-catalog" && (
        <>
          {/* ALBUM SELECTOR (Horizontal pills style) */}
          <div className="album-pill-selector">
            <span className="selector-label">Select Album:</span>
            <div className="pills-container">
              {albums.map((album) => {
                const coverThumb = album.cover_image || album.effective_cover_image;
                return (
                  <button
                    key={album.id}
                    type="button"
                    className={`album-pill-btn ${activeAlbum === album.id ? "active" : ""}`}
                    onClick={() => setActiveAlbum(album.id)}
                  >
                    {coverThumb ? (
                      <img
                        src={getImageUrl(coverThumb)}
                        alt=""
                        className="pill-cover-thumb"
                      />
                    ) : (
                      <span className="pill-folder-icon">📁</span>
                    )}
                    <span>{album.name}</span>
                    <span className="pill-photo-count">({album.photo_count || 0})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* GALLERY IMAGES GRID */}
          <section className="admin-section gallery-section">
            <div className="section-header-area">
              <div className="title-info">
                <h2>
                  <i className="bi bi-images"></i> Album Photos: {albums.find((a) => a.id === activeAlbum)?.name || "No Album Selected"}
                </h2>
                <p>
                  Viewing {mediaItems.length} photo(s) in this album.
                  {albums.find((a) => a.id === activeAlbum)?.cover_image ? (
                    <span className="cover-status-badge has-cover">
                      <i className="bi bi-star-fill"></i> Custom Folder Cover Active
                    </span>
                  ) : albums.find((a) => a.id === activeAlbum)?.effective_cover_image ? (
                    <span className="cover-status-badge auto-cover">
                      <i className="bi bi-info-circle-fill"></i> Using First Photo as Default Cover
                    </span>
                  ) : null}
                </p>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                {activeAlbum && (
                  <button
                    type="button"
                    className="delete-album-btn"
                    onClick={() => deleteAlbum(activeAlbum, albums.find((a) => a.id === activeAlbum)?.name)}
                    title="Delete this album and all its images"
                  >
                    <i className="bi bi-trash3-fill"></i> Delete Album
                  </button>
                )}
                <button
                  type="button"
                  className="folder-upload-shortcut-btn"
                  onClick={() => folderInputRef.current?.click()}
                  title="Upload an entire folder of photos"
                >
                  <i className="bi bi-folder-plus"></i> Upload Entire Folder
                </button>
                <button
                  type="button"
                  className="bulk-upload-shortcut-btn"
                  onClick={() => filesInputRef.current?.click()}
                  title="Select multiple photos to upload"
                >
                  <i className="bi bi-images"></i> Bulk Select Photos
                </button>
                <button
                  type="button"
                  className="upload-shortcut-btn"
                  onClick={() => setActiveTab("manage-albums")}
                  title="Open upload manager"
                >
                  <i className="bi bi-upload"></i> Manage Uploads
                </button>
              </div>
            </div>

            {loading && (
              <div className="loading-box">
                <div className="spinner"></div>
                <p>Loading images...</p>
              </div>
            )}

            {!loading && mediaItems.length === 0 && (
              <div className="empty-box">
                <i className="bi bi-image-alt"></i>
                <p>No images found inside this album. Go to the "Manage & Upload" tab to add photos!</p>
              </div>
            )}

            <div className="gallery-grid">
              {mediaItems.map((item) => {
                const currentAlb = albums.find((a) => a.id === activeAlbum);
                const activeCover = currentAlb?.cover_image || currentAlb?.effective_cover_image;
                const isCover = Boolean(activeCover && (activeCover === item.file_path || activeCover.endsWith(item.file_path)));

                return (
                  <div className={`admin-entry-card ${isCover ? "is-cover-item" : ""}`} key={item.id}>
                    <div className="gallery-image-wrapper">
                      {isCover && (
                        <div className="cover-ribbon-badge">
                          <i className="bi bi-star-fill"></i> Folder Cover
                        </div>
                      )}
                      <img
                        src={getImageUrl(item.file_path)}
                        alt={item.title || "Gallery"}
                      />
                    </div>
                    <div className="gallery-content">
                      <div className="gallery-card-actions">
                        {isCover ? (
                          <button
                            type="button"
                            className="cover-action-btn current-cover-btn"
                            disabled
                            title="This photo is currently the folder cover image"
                          >
                            <i className="bi bi-star-fill"></i> Cover Photo
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="cover-action-btn set-cover-btn"
                            onClick={() => setAsAlbumCover(item.file_path)}
                            title="Set this photo as the folder's cover image"
                          >
                            <i className="bi bi-star"></i> Set as Cover
                          </button>
                        )}
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => deleteMedia(item.id)}
                          title="Delete photo"
                        >
                          <i className="bi bi-trash-fill"></i> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* ================= MANAGE & UPLOAD TAB ================= */}
      {activeTab === "manage-albums" && (
        <div className="manage-tab-split-grid">
          
          {/* Column 1: Create Album & Edit Album List */}
          <div className="manage-column">
            <section className="admin-section">
              <div className="section-title">
                <h2><i className="bi bi-folder-plus"></i> Create & Edit Albums</h2>
                <p>Create new spiritual photo categories or modify existing ones</p>
              </div>

              <div className="album-create-box">
                <input
                  type="text"
                  placeholder="Enter New Album Name..."
                  value={newAlbum}
                  onChange={(e) => setNewAlbum(e.target.value)}
                />
                <button className="create-btn" onClick={addAlbum}>
                  Create Album
                </button>
              </div>

              {/* EDIT BOX */}
              {editAlbumId && (
                <div className="edit-box">
                  <h3>Edit Album Title</h3>
                  <input
                    type="text"
                    value={editAlbumName}
                    onChange={(e) => setEditAlbumName(e.target.value)}
                  />
                  <div className="edit-actions">
                    <button type="button" className="save-changes-btn" onClick={updateAlbum}>
                      Save Changes
                    </button>
                    <button
                      type="button"
                      className="cancel"
                      onClick={() => {
                        setEditAlbumId(null);
                        setEditAlbumName("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ALBUM LIST TABLE-LIKE */}
              <div className="albums-edit-list">
                {albums.map((album) => {
                  const coverThumb = album.cover_image || album.effective_cover_image;
                  return (
                    <div key={album.id} className="album-edit-row">
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: 1 }}>
                        {coverThumb ? (
                          <img
                            src={getImageUrl(coverThumb)}
                            alt=""
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: "1.5px solid #e2e8f0",
                              flexShrink: 0
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "8px",
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "20px",
                              flexShrink: 0
                            }}
                          >
                            📁
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <span className="album-title-text" style={{ display: "block" }}>{album.name}</span>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                            {album.photo_count || 0} photo(s) • {album.cover_image ? "★ Custom cover" : (album.effective_cover_image ? "Default cover" : "No photos")}
                          </div>
                        </div>
                      </div>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="edit-row-btn"
                          onClick={() => {
                            setEditAlbumId(album.id);
                            setEditAlbumName(album.name);
                          }}
                          title="Edit album title"
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <button
                          type="button"
                          className="delete-row-btn"
                          onClick={() => deleteAlbum(album.id, album.name)}
                          title={`Delete folder "${album.name}"`}
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {/* Column 2: Upload Media Files / Entire Folder */}
          <div className="manage-column">
            <section className="admin-section">
              <div className="section-title">
                <h2><i className="bi bi-cloud-arrow-up-fill"></i> Upload Photos & Folders</h2>
                <p>Upload an entire folder or bulk photos directly to your albums</p>
              </div>

              {/* Staged Upload View (if files are selected) */}
              {stagedFiles.length > 0 ? (
                <div className="staged-upload-card">
                  <div className="staged-header">
                    <div className="staged-info">
                      <div className="staged-icon">
                        <i className={stagedFolderName ? "bi bi-folder-check" : "bi bi-images"}></i>
                      </div>
                      <div>
                        <h4>{stagedFolderName ? `Folder: "${stagedFolderName}"` : `Selected Photos Batch`}</h4>
                        <span className="staged-sub">
                          <strong>{stagedFiles.length}</strong> photos • {(stagedFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(1)} MB total
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="clear-staged-btn"
                      onClick={clearStagedFiles}
                      disabled={uploading}
                    >
                      <i className="bi bi-x-circle"></i> Clear
                    </button>
                  </div>

                  {/* Album Destination Options */}
                  <div className="staged-destination-box">
                    <div
                      className={`dest-option-card ${!folderAutoAlbum ? "selected" : ""}`}
                      onClick={() => !uploading && setFolderAutoAlbum(false)}
                    >
                      <label className="dest-option" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="radio"
                          name="album_dest"
                          checked={!folderAutoAlbum}
                          onChange={() => setFolderAutoAlbum(false)}
                          disabled={uploading}
                        />
                        <span>Upload into existing album:</span>
                      </label>
                      {!folderAutoAlbum && (
                        <div className="dest-input-subwrapper" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={activeAlbum || ""}
                            onChange={(e) => setActiveAlbum(Number(e.target.value))}
                            className="album-select-dropdown"
                            disabled={uploading}
                          >
                            <option value="">-- Choose Target Album --</option>
                            {albums.map((album) => (
                              <option key={album.id} value={album.id}>
                                📁 {album.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div
                      className={`dest-option-card ${folderAutoAlbum ? "selected" : ""}`}
                      onClick={() => !uploading && setFolderAutoAlbum(true)}
                    >
                      <label className="dest-option" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="radio"
                          name="album_dest"
                          checked={folderAutoAlbum}
                          onChange={() => setFolderAutoAlbum(true)}
                          disabled={uploading}
                        />
                        <span>Create new album automatically:</span>
                      </label>
                      {folderAutoAlbum && (
                        <div className="dest-input-subwrapper" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            className="custom-album-input"
                            placeholder="Enter Album Name for this Folder..."
                            value={customAlbumName}
                            onChange={(e) => setCustomAlbumName(e.target.value)}
                            disabled={uploading}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Thumbnail Previews */}
                  <div className="staged-thumbnails-grid">
                    {previewThumbnails.map((thumb, idx) => (
                      <div key={idx} className="staged-thumb-wrapper" title={`${thumb.name} (${thumb.size} MB)`}>
                        <img src={thumb.url} alt={thumb.name} />
                      </div>
                    ))}
                    {stagedFiles.length > previewThumbnails.length && (
                      <div className="staged-more-badge">
                        +{stagedFiles.length - previewThumbnails.length} more
                      </div>
                    )}
                  </div>

                  {/* Progress Bar (Visible while uploading) */}
                  {uploadProgress.inProgress && (
                    <div className="upload-progress-container">
                      <div className="progress-info-row">
                        <span>{uploadProgress.statusText}</span>
                        <strong>{uploadProgress.percent}%</strong>
                      </div>
                      <div className="progress-bar-track">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${uploadProgress.percent}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Start Upload Button */}
                  <button
                    type="button"
                    className="start-batch-upload-btn"
                    onClick={uploadBatchMedia}
                    disabled={
                      uploading ||
                      (!folderAutoAlbum && !activeAlbum) ||
                      (folderAutoAlbum && !customAlbumName.trim())
                    }
                  >
                    {uploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Uploading {uploadProgress.uploaded}/{uploadProgress.total} Photos...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                        Start Upload ({stagedFiles.length} Photos)
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Dropzone View (When no files are staged) */
                <div className="upload-box-wrapper">
                  <div
                    className={`gallery-upload-dropzone ${isDragging ? "dragging" : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="dropzone-icon">
                      <i className="bi bi-folder-symlink-fill"></i>
                    </div>
                    <h3>Upload Entire Folder or Multiple Photos</h3>
                    <p>Drag and drop a photo folder from your computer, or click below to browse</p>

                    <div className="dropzone-actions">
                      <button
                        type="button"
                        className="btn-select-folder"
                        onClick={() => folderInputRef.current?.click()}
                      >
                        <i className="bi bi-folder2-open"></i> Choose Entire Folder
                      </button>
                      <button
                        type="button"
                        className="btn-select-files"
                        onClick={() => filesInputRef.current?.click()}
                      >
                        <i className="bi bi-images"></i> Select Multiple Photos
                      </button>
                    </div>

                    <small className="dropzone-hint">
                      Supports JPG, JPEG, PNG, WEBP, GIF • Full folder structure is scanned automatically
                    </small>
                  </div>
                </div>
              )}
            </section>
          </div>

        </div>
      )}
    </div>
  );
}

export default AdminGallery;
